import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {
  WicketCheckoutResponse,
  WicketOfferItem,
  WicketStripeSessionResponse,
  WicketUser,
} from '@tisa-group/models';
import {
  TISA_WICKET_ENTITLEMENTS_API_URL,
  TISA_WICKET_V2_API_URL,
} from '../tisa-wicket.providers';
import {Observable} from 'rxjs';
import {GooglePlayOrderResponse} from '@tisa-group/models';

@Injectable({
  providedIn: 'root',
})
export class ClientPaymentsApiService {
  constructor(
    protected readonly httpClient: HttpClient,
    @Inject(TISA_WICKET_V2_API_URL) readonly providedWicketV2Url: string | (() => string),
    @Inject(TISA_WICKET_ENTITLEMENTS_API_URL)
    readonly wicketEntitlementsUrl: string | (() => string),
  ) {}

  fetchOfferItems(
    itemFamilly: string,
    prefferedLanguage: WicketUser['preferred_language'],
  ) {
    return this.httpClient.get<{data: WicketOfferItem[]; next_offset: string}>(
      `${this.wicketV2Url}/payments/item/price`,
      {
        params: {
          'status[in]': ['active'],
          'item_family_id[in]': itemFamilly,
          'channel[in]': 'web',
          preffered_language: prefferedLanguage as string,
        },
      },
    );
  }

  entitlementsInquiry(resource: string) {
    return this.httpClient.post(`${this.entitlementsUrl}/v1/inquiry`, {
      resource,
      action: 'read',
      // TODO: ask why the hell is the following required and hopefully remove it form API
      context: {
        valid_until: Math.round(new Date().getTime() / 1000),
      },
    });
  }

  entitlementsInquiryForToday(resource: string) {
    return this.httpClient.post(`${this.entitlementsUrl}/v1/inquiry-for-today`, {
      resource,
      action: 'read',
    });
  }

  checkoutSubscription(
    itemId: string,
    prefferedLanguage: string,
    quantity = 1,
    currency_code = 'EUR',
  ) {
    return this.httpClient.post<WicketCheckoutResponse>(
      `${this.wicketV2Url}/payments/user/hosted_pages/checkout`,
      {
        subscription_items: [
          {
            item_price_id: itemId,
            quantity: 1,
          },
        ],
        preferred_language: prefferedLanguage,
      },
    );
  }

  checkoutOneTimePayment(
    itemId: string,
    prefferedLanguage: string,
    quantity = 1,
    currency_code = 'EUR',
  ) {
    return this.httpClient.post<WicketCheckoutResponse>(
      `${this.wicketV2Url}/payments/user/hosted_pages/checkout_one_time`,
      {
        item_prices: [
          {
            item_price_id: itemId,
            quantity,
          },
        ],
        currency_code: currency_code,
        preferred_language: prefferedLanguage,
      },
    );
  }

  /**
   * Creates a Stripe-hosted checkout session for a one-time (PPV) payment.
   * The returned `url` is single-use — redirect the browser to it
   * immediately, never cache it.
   */
  createStripeCheckout(payload: {
    price_id: string;
    success_url: string;
    cancel_url: string;
    preferred_language?: string;
  }) {
    return this.httpClient.post<WicketStripeSessionResponse>(
      `${this.wicketV2Url}/payments/stripe/user/checkout`,
      payload,
    );
  }

  /**
   * Creates a Stripe customer portal session (payment history & invoices).
   * The returned `url` expires within minutes — request a fresh one per click.
   */
  createStripePortalSession(payload: {return_url: string; preferred_language?: string}) {
    return this.httpClient.post<WicketStripeSessionResponse>(
      `${this.wicketV2Url}/payments/stripe/user/portal_session`,
      payload,
    );
  }

  getChargeBeePortalSesion(redirect_url: string) {
    return this.httpClient.post<WicketCheckoutResponse>(
      `${this.wicketV2Url}/payments/user/portal_sessions/`,
      {redirect_url},
    );
  }

  confirmPayment(hostedPageId: string) {
    return this.httpClient.post(
      `${this.wicketV2Url}/payments/user/hosted_pages/${hostedPageId}/restore_entitlements`,
      {},
    );
  }

  getGooglePlaySubscriptions(page = 1, page_size = 20) {
    return this.httpClient.get(
      `${this.wicketV2Url}/payments/user/subscriptions/google_play`,
      {
        params: {
          page,
          page_size,
        },
      },
    );
  }

  getGooglePlayOrders(page = 1, pageSize = 20): Observable<GooglePlayOrderResponse> {
    return this.httpClient.get<GooglePlayOrderResponse>(
      `${this.wicketV2Url}/payments/user/orders/google_play`,
      {
        params: {
          page,
          page_size: pageSize,
        },
      },
    );
  }

  get entitlementsUrl() {
    return this.wicketEntitlementsUrl instanceof Function
      ? this.wicketEntitlementsUrl()
      : this.wicketEntitlementsUrl;
  }

  get wicketV2Url() {
    return this.providedWicketV2Url instanceof Function
      ? this.providedWicketV2Url()
      : this.providedWicketV2Url;
  }
}
